import { randomUUID } from "node:crypto";

import cleanStack from "clean-stack";
import terminalLink from "terminal-link";

import type { RootFlagsResult } from "../../commands/root.ts";
import type { Context } from "../command/context.ts";
import { env } from "../config/env.ts";
import { isAbortError, isObject } from "../util/is.ts";
import { serializeError } from "../util/object.ts";
import { workspaceRoot } from "../util/paths.ts";
import { isRetryableNetworkErrorCode } from "../util/retry.ts";
import { println } from "./print.ts";
import { initSentry, sendErrorToSentry } from "./sentry.ts";
import { sprint, sprintln, type SprintOptions } from "./sprint.ts";

export const reportErrorAndExit = async (ctx: Context, cause: unknown): Promise<never> => {
  if (isAbortError(cause)) {
    ctx.log.debug("aborting without reporting error", { error: cause });
    return process.exit(1);
  }

  ctx.log.error("reporting error and exiting", { error: cause });

  try {
    const error = GGTError.from(cause);
    error.print();

    if (error.isBug === IsBug.NO) {
      return undefined as never;
    }

    await sendErrorToSentry(error);
  } finally {
    process.exit(1);
  }
};

export const installErrorHandlers = async (ctx: Context, flags: RootFlagsResult): Promise<void> => {
  ctx.log.debug("installing error handlers");
  await initSentry(ctx, flags);

  const handleError = (error: unknown) => void reportErrorAndExit(ctx, error);
  process.once("uncaughtException", handleError);
  process.once("unhandledRejection", handleError);
};

export const IsBug = Object.freeze({
  YES: "yes",
  NO: "no",
  MAYBE: "maybe",
});

export type IsBug = (typeof IsBug)[keyof typeof IsBug];

/**
 * Base class for all errors.
 */
export abstract class GGTError extends Error {
  /**
   * The ID for this error.
   */
  id = env.testLike ? "00000000-0000-0000-0000-000000000000" : randomUUID();

  /**
   * The underlying *thing* that caused this error.
   */
  cause?: unknown;

  /**
   * Assume the stack trace exists.
   */
  override stack!: string;

  /**
   * Indicates whether this error is considered a bug or not.
   */
  abstract isBug: IsBug;

  constructor(message: string) {
    super(message);
    Error.captureStackTrace(this, this.constructor);
    this.stack = cleanStack(this.stack, { pretty: true, basePath: workspaceRoot });
  }

  /**
   * Constructs a GGTError from an unknown cause.
   *
   * @param cause - The cause of the error.
   */
  static from(cause: unknown): GGTError {
    if (cause instanceof GGTError) {
      return cause;
    }
    if (isRetryableNetworkErrorCode(cause) || (isObject(cause) && "cause" in cause && isRetryableNetworkErrorCode(cause.cause))) {
      return new NetworkError(cause);
    }
    return new UnexpectedError(cause);
  }

  sprint(): string {
    let content = this.render();

    if (this.isBug !== IsBug.NO) {
      // ensure the rendered message ends with a newline
      content = sprintln(content);

      const thisIsABug = this.isBug === IsBug.YES ? "This is a bug" : "If you think this is a bug";
      const issueLink = `https://github.com/gadget-inc/ggt/issues/new?template=bug_report.yml&error-id=${this.id}`;

      if (terminalLink.isSupported) {
        content += sprintln({
          ensureEmptyLineAbove: true,
          content: sprint`
            ${thisIsABug}, ${terminalLink("click here", issueLink)} to create an issue on GitHub.
          `,
        });
      } else {
        content += sprintln({
          ensureEmptyLineAbove: true,
          content: sprint`
            ${thisIsABug}, use the link below to create an issue on GitHub.

            ${issueLink}
          `,
        });
      }
    }

    return content;
  }

  print(options?: SprintOptions): void {
    println({ ensureEmptyLineAbove: true, ...options, content: this.sprint() });
  }

  /**
   * Turns this error into a user-friendly message that explains what
   * went wrong and how to fix it. A good write up of what an error
   * should look like can be found here:
   * {@link https://clig.dev/#errors}
   */
  protected abstract render(): string;
}

/**
 * Our "catch all" error.
 *
 * If this error is thrown, we almost certainly have a bug, and should
 * either fix it or add a more specific error so that we can provide
 * more useful information.
 */
export class UnexpectedError extends GGTError {
  isBug = IsBug.YES;

  constructor(cause: unknown) {
    super("An unexpected error occurred");
    this.cause = cause;
  }

  protected render(): string {
    const serialized = serializeError(this.cause);
    const body = serialized.stack || serialized.message || this.stack;
    return sprint`
      ${this.message}.

      ${body}
    `;
  }
}

/**
 * A network connectivity error (DNS failure, connection refused, etc.).
 *
 * Not a bug — the user's network is unavailable or cannot reach Gadget.
 */
export class NetworkError extends GGTError {
  isBug = IsBug.MAYBE;

  constructor(cause: unknown) {
    super("A network error occurred");
    this.cause = cause;
  }

  protected render(): string {
    return sprint`
      A network error occurred while communicating with Gadget.

      Please check your internet connection and try again.
    `;
  }
}

/**
 * An error that is expected to happen sometimes.
 */
export class EdgeCaseError extends GGTError {
  isBug = IsBug.MAYBE;

  override cause?: unknown;

  constructor(message: string, cause?: unknown) {
    super(message);
    this.cause = cause;
  }

  protected render(): string {
    return this.message;
  }
}
